import React from 'react';
import { Input } from '../../../../shared/components/Input';

/**
 * Slide-over drawer that makes the welding configuration editable per proposal.
 * It does not change how welding is priced or displayed: each field is prefilled
 * from the global welding config, and editing one stores a per-proposal override
 * keyed "group.field" (e.g. "hardware.unitCost") that the backend merges over the
 * config for this proposal only. Welding's own math and breakdown stay as they
 * are. Percentages are whole numbers here, matching how the welding config stores
 * them (utilization 80, margin 25, discount 10).
 */

export type WeldingConfigGroups = Partial<
  Record<'hardware' | 'application' | 'other_costs' | 'discount', Record<string, number>>
>;

interface ConfigField {
  group: 'hardware' | 'application' | 'other_costs' | 'discount';
  key: string;
  label: string;
  step?: number;
  tooltip: string;
  // A computed default for fields that are not a raw config value (the hardware
  // monthly rental is derived from the unit-cost build-up).
  defaultFrom?: (config?: WeldingConfigGroups) => number;
}

// The monthly rental per unit the hardware build-up currently produces, so the
// field prefills with today's number. Mirrors hardware.py (gross margin):
//   pricePerUnit = (unitCost / (1 - margin)) / utilization
//   monthlyRental = pricePerUnit x rentalRate
const monthlyRentalPerUnit = (config?: WeldingConfigGroups): number => {
  const h = config?.hardware ?? {};
  const unitCost = h.unitCost ?? 0;
  const margin = (h.profitMargin ?? 0) / 100;
  const utilization = (h.utilization ?? 0) / 100;
  const rentalRate = (h.rentalRate ?? 0) / 100;
  if (utilization <= 0 || margin >= 1) return 0;
  const rental = ((unitCost / (1 - margin)) / utilization) * rentalRate;
  return Math.round(rental * 100) / 100;
};

// The per-unit-per-month software fee the recovery build-up currently produces,
// so the field prefills with today's number. Mirrors welding.py (gross margin):
//   costPerUnit = (developmentCost / payBackPeriod) / unitsPerYear
//   fee = costPerUnit / (1 - margin)
const softwareFeePerUnit = (config?: WeldingConfigGroups): number => {
  const a = config?.application ?? {};
  const dev = a.developmentCost ?? 0;
  const payback = a.payBackPeriod ?? 0;
  const units = a.unitsPerYear ?? 0;
  const margin = (a.profitMargin ?? 0) / 100;
  if (payback <= 0 || units <= 0 || margin >= 1) return 0;
  const fee = (dev / payback / units) / (1 - margin);
  return Math.round(fee * 100) / 100;
};

interface Group {
  title: string;
  fields: ConfigField[];
}

const GROUPS: Group[] = [
  {
    title: 'Hardware',
    fields: [
      {
        group: 'hardware',
        key: 'monthlyRentalPerUnit',
        label: 'Monthly rental / unit $',
        step: 10,
        tooltip: 'Monthly rental charged per hardware unit. Overriding this sets the rental directly and bypasses the unit-cost, utilization, and margin build-up.',
        defaultFrom: monthlyRentalPerUnit,
      },
    ],
  },
  {
    title: 'Software',
    fields: [
      {
        group: 'application',
        key: 'softwareFeePerUnit',
        label: 'Software fee / unit / month $',
        step: 10,
        tooltip: 'Monthly software fee charged per unit. Overriding this sets the fee directly and bypasses the dev-cost, payback, units, and margin build-up.',
        defaultFrom: softwareFeePerUnit,
      },
    ],
  },
  {
    title: 'Duration discount',
    fields: [
      { group: 'discount', key: 'lessThan12Month', label: '<= 12 mo %', step: 1, tooltip: 'Discount for 12 months or less.' },
      { group: 'discount', key: 'month13To24', label: '13-24 mo %', step: 1, tooltip: 'Discount for 13 to 24 months.' },
      { group: 'discount', key: 'month25To36', label: '25-36 mo %', step: 1, tooltip: 'Discount for 25 to 36 months.' },
      { group: 'discount', key: 'month37To60', label: '37-60 mo %', step: 1, tooltip: 'Discount for 37 to 60 months.' },
      { group: 'discount', key: 'perpetual', label: 'Perpetual %', step: 1, tooltip: 'Discount for a perpetual licence.' },
    ],
  },
  {
    title: 'Internet & cloud',
    fields: [
      { group: 'other_costs', key: 'internetFee', label: 'Internet $/mo', step: 1, tooltip: 'Monthly internet charge per connected unit.' },
      { group: 'other_costs', key: 'awsCompute', label: 'Cloud compute $/mo', step: 1, tooltip: 'Monthly cloud compute cost per connected unit.' },
      { group: 'other_costs', key: 'awsStorage', label: 'Cloud storage $/mo', step: 1, tooltip: 'Monthly cloud storage cost per connected unit.' },
      { group: 'other_costs', key: 'liveModeInternetCloudMultiplier', label: 'Live mode x', step: 0.1, tooltip: 'Internet/cloud multiplier for live-mode units.' },
      { group: 'other_costs', key: 'offlineModeInternetCloudMultiplier', label: 'Offline mode x', step: 0.1, tooltip: 'Internet/cloud multiplier for offline-only units.' },
    ],
  },
  {
    title: 'Services',
    fields: [
      { group: 'other_costs', key: 'maintenanceAndSupport', label: 'Support $/hr', step: 1, tooltip: 'Remote support hourly rate.' },
      { group: 'other_costs', key: 'trainingRate', label: 'Training $/hr', step: 1, tooltip: 'Training hourly rate.' },
      { group: 'other_costs', key: 'trainingQuickHours', label: 'Quick hrs', step: 0.5, tooltip: 'Hours for a quick training session.' },
      { group: 'other_costs', key: 'trainingMediumHours', label: 'Medium hrs', step: 0.5, tooltip: 'Hours for a medium training session.' },
      { group: 'other_costs', key: 'trainingLongHours', label: 'Long hrs', step: 0.5, tooltip: 'Hours for a long training session.' },
      { group: 'other_costs', key: 'provisioningHourlyRate', label: 'Provisioning $/hr', step: 1, tooltip: 'Provisioning hourly rate.' },
      { group: 'other_costs', key: 'onFieldSupportDailyRate', label: 'On-field $/day', step: 10, tooltip: 'On-field support daily rate.' },
    ],
  },
  {
    title: 'Shipping',
    fields: [
      { group: 'other_costs', key: 'unitWeightKg', label: 'Unit weight kg', step: 0.5, tooltip: 'Weight per unit, for mobilization shipping.' },
      { group: 'other_costs', key: 'shippingCostPerKg', label: 'Shipping $/kg', step: 0.5, tooltip: 'Shipping cost per kilogram.' },
    ],
  },
];

// Duration discount bands, matching the backend duration_discount_pct: the band
// is the first whose upper bound the proposal duration falls within.
const DISCOUNT_BANDS: Array<{ key: string; maxMonths: number }> = [
  { key: 'lessThan12Month', maxMonths: 12 },
  { key: 'month13To24', maxMonths: 24 },
  { key: 'month25To36', maxMonths: 36 },
  { key: 'month37To60', maxMonths: 60 },
  { key: 'perpetual', maxMonths: Infinity },
];

const activeDiscountKey = (months: number): string =>
  (DISCOUNT_BANDS.find((b) => months <= b.maxMonths) ?? DISCOUNT_BANDS[DISCOUNT_BANDS.length - 1]).key;

interface Props {
  open: boolean;
  onClose: () => void;
  config?: WeldingConfigGroups;
  overrides: Record<string, number>;
  onOverrideChange: (key: string, value: number | undefined) => void;
  onResetAll: () => void;
  // Proposal duration in months. The Duration discount section shows only the
  // band this duration falls into, instead of all five bands.
  durationMonths?: number;
}

const overrideKeyOf = (field: ConfigField): string => `${field.group}.${field.key}`;

export const WeldingConfigDrawer: React.FC<Props> = ({
  open,
  onClose,
  config,
  overrides,
  onOverrideChange,
  onResetAll,
  durationMonths,
}) => {
  if (!open) return null;

  const defaultValue = (field: ConfigField): number =>
    field.defaultFrom ? field.defaultFrom(config) : config?.[field.group]?.[field.key] ?? 0;

  // Only the discount band the proposal's duration falls into is shown. When the
  // duration is unknown, fall back to the entry band so a value is still visible.
  const bandKey = activeDiscountKey(durationMonths && durationMonths > 0 ? durationMonths : 0);
  const visibleFields = (group: Group): ConfigField[] =>
    group.title === 'Duration discount'
      ? group.fields.filter((f) => f.key === bandKey)
      : group.fields;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Welding configuration">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[#E2E5EA] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold font-heading text-blueprint-blue">Welding configuration</h3>
            <p className="mt-0.5 text-xs text-heritage-grey">
              From the active configuration. Edit any value to override it for this proposal only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-medium text-blueprint-blue">
                {group.title}
                {group.title === 'Duration discount' && durationMonths ? (
                  <span className="ml-2 text-xs font-normal text-heritage-grey">
                    {durationMonths} month{durationMonths === 1 ? '' : 's'} for this proposal
                  </span>
                ) : null}
              </h4>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
                {visibleFields(group).map((field) => {
                  const key = overrideKeyOf(field);
                  const override = overrides[key];
                  const isOverridden = override !== undefined;
                  const def = defaultValue(field);
                  const shown = isOverridden ? override : def;
                  return (
                    <div key={key}>
                      <Input
                        id={`welding-cfg-${key}`}
                        label={field.label}
                        tooltip={field.tooltip}
                        type="number"
                        step={field.step ?? 'any'}
                        value={shown}
                        className={isOverridden ? 'border-primary' : ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            onOverrideChange(key, undefined);
                            return;
                          }
                          const n = Number(raw);
                          if (Number.isNaN(n)) return;
                          onOverrideChange(key, n);
                        }}
                      />
                      <div className="mt-0.5 flex items-center justify-between text-[11px] text-heritage-grey">
                        <span>default {def}</span>
                        {isOverridden && (
                          <button type="button" className="text-primary hover:underline" onClick={() => onOverrideChange(key, undefined)}>
                            reset
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[#E2E5EA] px-5 py-3">
          <button type="button" onClick={onResetAll} className="text-sm text-text-grey hover:text-blueprint-blue">
            Reset all to defaults
          </button>
          <button type="button" onClick={onClose} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
